/*
 * RealAgentTransport — SSE-Stream gegen den Carbisdale-Server.
 *
 * Konkrete Implementierung von AgentTransport. Nutzt fetch + ReadableStream
 * fuer SSE (POST, kein EventSource, weil der Body kein GET-Pfad hat).
 *
 * Lifecycle:
 *   - sendMessage(message, context, handlers) — startet einen ReAct-Turn
 *   - reportActionDone({...}) — POST /api/agent/action-done (NUR fuer
 *     Auto-Apply-Tools; Review-Verdicts werden vom AgentService gepuffert
 *     und beim naechsten sendMessage als reviewResults im Body mitgeschickt)
 *   - abort() — bricht den lokalen fetch ab UND POST /api/agent/abort
 *     (Multi-Device-tauglich)
 *   - switchProject(projectId) — pro Tab-Lebenszyklus Erst-Touch-Reset
 *
 * Session-Management: pro Projekt eine sessionId. Beim Projekt-Swap wird
 * die sessionId getauscht. Beim Erst-Touch eines Projekts pro Tab feuern
 * wir POST /api/agent/session/reset, damit Client + Server denselben leeren
 * Kontext haben (Spec §5).
 */

import { AgentTransport } from './AgentTransport.js'
import { handleSessionLost } from '../auth/AuthService.js'
import { AGENT } from '../config.js'

export class RealAgentTransport extends AgentTransport {
    constructor(serverUrl = '') {
        super()
        this.serverUrl = serverUrl
        this._sessionIdsByProject = new Map()
        this._currentProjectId = null
        this._abortController = null
        // Erst-Touch-Tracking pro Tab-Lebenszyklus.
        this._resetProjects = new Set()
        // Phase 4.5: aktiver await_review-Slot. Wird vom await_review_started-
        // SSE gesetzt, vom await_review_done geleert. Steuert, ob der Client
        // Verdicts via reportReviewDone (POST /review-done) oder via Puffer +
        // naechstem /message schickt.
        this._awaitReviewActive = false
    }

    /**
     * @returns {boolean} ob der Server-Loop gerade auf Review-Verdicts wartet
     *                    (await_review-Tool aufgerufen, await_review_done
     *                    noch nicht angekommen).
     */
    hasActiveAwaitReview() {
        return this._awaitReviewActive
    }

    async switchProject(projectId) {
        if (this._currentProjectId === projectId) return
        this._currentProjectId = projectId
        if (!this._resetProjects.has(projectId)) {
            this._resetProjects.add(projectId)
            try {
                await fetch(`${this.serverUrl}/api/agent/session/reset`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projectId })
                })
            } catch (err) {
                console.warn('Agent session reset failed:', err?.message)
            }
        }
    }

    /**
     * Sendet eine User-Message und streamt die Antwort. cursorContext und
     * reviewResults werden via context durchgereicht.
     *
     * @param {string} message
     * @param {{ projectId?: string, cursorContext?: object, reviewResults?: Array }} context
     * @param {object} handlers - alle Handler aus AgentTransport
     */
    async sendMessage(message, context, handlers) {
        // Bestehenden Stream abbrechen, falls einer laeuft
        this.abort()

        const projectId = context?.projectId || this._currentProjectId
        const sessionId = projectId ? this._sessionIdsByProject.get(projectId) : null

        const body = {
            message,
            ...(projectId ? { projectId } : {}),
            ...(sessionId ? { sessionId } : {}),
            ...(context?.cursorContext ? { cursorContext: context.cursorContext } : {}),
            ...(context?.reviewResults && context.reviewResults.length > 0
                ? { reviewResults: context.reviewResults } : {})
        }

        this._abortController = new AbortController()

        let response
        try {
            response = await fetch(`${this.serverUrl}/api/agent/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                signal: this._abortController.signal
            })
        } catch (err) {
            if (err?.name === 'AbortError') {
                handlers?.onAborted?.({ reason: 'user_requested' })
            } else {
                handlers?.onError?.(err)
            }
            return
        }

        if (!response.ok) {
            // 401: Session abgelaufen — Reload zur Login-Seite triggern
            if (response.status === 401) handleSessionLost()
            handlers?.onError?.(new Error(`Server error: ${response.status}`))
            return
        }

        await this._processSSE(response, handlers, projectId)
    }

    /**
     * Meldet das Verdict eines AUTO-APPLY-Tools. Review-Verdicts laufen NICHT
     * ueber diesen Pfad — sie werden vom AgentService gepuffert und beim
     * naechsten sendMessage im Body mitgeschickt (Spec §6).
     */
    async reportActionDone(info) {
        const { toolCallId, result } = info || {}
        const projectId = this._currentProjectId
        const sessionId = projectId ? this._sessionIdsByProject.get(projectId) : null
        if (!sessionId) {
            // Sollte praktisch nie vorkommen — wenn doch, hilft das Log beim
            // Debuggen: ein Auto-Apply-Verdict ohne Session geht ins Leere
            // und der Server-Loop timed out spaeter.
            console.warn(
                '[RealAgentTransport] reportActionDone with no session',
                { toolCallId, result, projectId }
            )
            return
        }
        // Defensive: alle relevanten Phase-3-Felder durchreichen — der Server
        // gibt sie als verdict-Object an `awaitActionDone(toolCallId)` weiter.
        const body = { sessionId, toolCallId, result }
        for (const key of [
            'appliedVersion', 'newParagraphId', 'paragraphIds',
            'separatorId', 'firstParagraphId', 'paragraphCount',
            'diagnostics'  // Review v1.4 #11/#15: Reason-Code-Mapping
        ]) {
            if (info && info[key] != null) body[key] = info[key]
        }
        // Verlorener Ack = 5-Minuten-action_timeout im Server-Loop, obwohl der
        // Block laengst angewendet ist — der Agent haelt ihn dann fuer nicht
        // angewendet und legt Duplikate an (Incident 2026-07-14). Deshalb:
        // Status pruefen und bei 429/5xx/Netzwerkfehler einmal retrien. Der
        // POST ist idempotent — der Server resolved nur ein pending Promise,
        // ein Doppel-Ack antwortet harmlos mit no_pending.
        for (let attempt = 1; attempt <= AGENT.actionDoneAttempts; attempt++) {
            try {
                const res = await fetch(`${this.serverUrl}/api/agent/action-done`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                })
                if (res.ok) return
                console.warn(
                    `action-done responded ${res.status} (attempt ${attempt}/${AGENT.actionDoneAttempts})`,
                    { toolCallId }
                )
            } catch (err) {
                console.warn(
                    `action-done request failed (attempt ${attempt}/${AGENT.actionDoneAttempts}):`,
                    err?.message
                )
            }
            if (attempt < AGENT.actionDoneAttempts) {
                await new Promise(r => setTimeout(r, AGENT.actionDoneRetryDelayMs))
            }
        }
    }

    /**
     * Phase 4.5: meldet Review-Verdicts an einen offenen `await_review`-Waiter
     * (POST /api/agent/review-done). Der ReviewController nutzt das nur, wenn
     * `hasActiveAwaitReview()` true ist — sonst werden Verdicts wie bisher
     * gepuffert und im naechsten /message als reviewResults mitgeschickt.
     *
     * @param {Array<{toolCallId, result, appliedVersion?, diagnostics?}>} reviewResults
     * @returns {Promise<{status:string, accepted?:number, complete?:boolean}|null>}
     */
    async reportReviewDone(reviewResults) {
        const projectId = this._currentProjectId
        const sessionId = projectId ? this._sessionIdsByProject.get(projectId) : null
        if (!sessionId) {
            console.warn('[RealAgentTransport] reportReviewDone with no session')
            return null
        }
        if (!Array.isArray(reviewResults) || reviewResults.length === 0) return null
        try {
            const res = await fetch(`${this.serverUrl}/api/agent/review-done`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId, reviewResults })
            })
            if (!res.ok) {
                console.warn('[RealAgentTransport] review-done responded', res.status)
                return null
            }
            return await res.json()
        } catch (err) {
            console.warn('review-done request failed:', err?.message)
            return null
        }
    }

    /**
     * Bricht den laufenden Loop ab. Lokal via fetch-Abort, zusaetzlich POST
     * /api/agent/abort (Multi-Device-tauglich).
     */
    abort() {
        const projectId = this._currentProjectId
        const sessionId = projectId ? this._sessionIdsByProject.get(projectId) : null
        // Lokaler Abort beendet den SSE-Stream — kein await_review_done mehr,
        // also Flag selbst zuruecksetzen, sonst klemmt der naechste Submit.
        this._awaitReviewActive = false
        if (this._abortController) {
            this._abortController.abort()
            this._abortController = null
        }
        if (sessionId) {
            // Fire-and-forget — Server-Loop pollt das Flag.
            fetch(`${this.serverUrl}/api/agent/abort`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId })
            }).catch(() => {})
        }
    }

    /**
     * Beendet die Server-Session (fire-and-forget, wird in main.js bei
     * beforeunload / Tab-Close gerufen).
     */
    endSession() {
        const projectId = this._currentProjectId
        const sessionId = projectId ? this._sessionIdsByProject.get(projectId) : null
        if (!sessionId) return
        try {
            fetch(`${this.serverUrl}/api/agent/end`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId }),
                keepalive: true
            }).catch(() => {})
        } catch { /* swallow */ }
    }

    destroy() {
        this.endSession()
        this.abort()
    }

    // ─── SSE-Pipeline ──────────────────────────────────────────────────────

    async _processSSE(response, handlers, projectId) {
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        try {
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                const events = buffer.split('\n\n')
                buffer = events.pop()
                for (const raw of events) {
                    if (!raw.trim()) continue
                    const parsed = parseSSEEvent(raw)
                    if (!parsed.event) continue
                    this._dispatch(parsed.event, parsed.data, handlers, projectId)
                }
            }
        } catch (err) {
            if (err?.name === 'AbortError') {
                handlers?.onAborted?.({ reason: 'user_requested' })
            } else {
                handlers?.onError?.(err)
            }
        } finally {
            reader.releaseLock?.()
        }
    }

    _dispatch(event, data, handlers, projectId) {
        switch (event) {
            case 'session':
                if (projectId) this._sessionIdsByProject.set(projectId, data.sessionId)
                handlers?.onSession?.({ sessionId: data.sessionId })
                break
            case 'session_expired':
                // Legacy-Pfad: Server erniert nicht mehr hart (Graceful Recovery,
                // siehe `session_recovered`). Bleibt fuer aeltere Server stehen —
                // lokale sessionId verwerfen, naechster sendMessage baut frisch auf.
                if (projectId) this._sessionIdsByProject.delete(projectId)
                handlers?.onError?.(new Error('Session expired'))
                break
            case 'session_recovered':
                // Server hat die abgelaufene/unbekannte Session transparent durch
                // eine frische ersetzt und faehrt mit dem Prompt fort. Die neue
                // sessionId folgt unmittelbar im `session`-Event — hier nur die
                // dezente Notiz nach oben reichen, kein Error, kein Abbruch.
                handlers?.onSessionRecovered?.(data)
                break
            case 'thinking':       handlers?.onThinking?.(data); break
            case 'thinking_done':  handlers?.onThinkingDone?.(); break
            case 'searching':      handlers?.onSearching?.(); break
            case 'delta':          handlers?.onDelta?.(data); break
            case 'tool_call':      handlers?.onToolCall?.(data); break
            case 'tool_result':    handlers?.onToolResult?.(data); break

            // Review-pflichtige Vorschlaege (Haupttext) — Spec §6
            case 'propose_rewrite':       handlers?.onProposeRewrite?.(data); break
            case 'propose_insert':        handlers?.onProposeInsert?.(data); break
            case 'propose_delete':        handlers?.onProposeDelete?.(data); break

            // Auto-Apply: Background-Inhalt (Phase 3)
            case 'propose_rewrite_apply': handlers?.onProposeRewriteApply?.(data); break
            case 'propose_insert_apply':  handlers?.onProposeInsertApply?.(data); break
            case 'propose_delete_apply':  handlers?.onProposeDeleteApply?.(data); break

            // Auto-Apply: Skizze, Notes-Edit, Section-Tools (Phase 3)
            case 'propose_sketch':         handlers?.onProposeSketch?.(data); break
            case 'propose_write_note':     handlers?.onProposeWriteNote?.(data); break
            case 'propose_create_section': handlers?.onProposeCreateSection?.(data); break
            case 'propose_update_section': handlers?.onProposeUpdateSection?.(data); break
            case 'propose_delete_section': handlers?.onProposeDeleteSection?.(data); break

            // Auto-Apply: anker-basierte Moves (move-tool-specification.md)
            case 'propose_move_section':   handlers?.onProposeMoveSection?.(data); break
            case 'propose_move_paragraph': handlers?.onProposeMoveParagraph?.(data); break

            // Auto-Apply: User-Memory (Phase 4 Sub-Phase 4.1).
            case 'propose_update_user_memory':
                handlers?.onProposeUpdateUserMemory?.(data)
                break

            // Auto-Apply: Context-Info + Section-Jump (Phase 4 Sub-Phase 4.2).
            // set_context_link emittiert intern propose_update_context_info
            // (Variante A) — kein eigener Event-Type noetig.
            case 'propose_update_context_info':
                handlers?.onProposeUpdateContextInfo?.(data)
                break
            case 'propose_jump_to_section':
                handlers?.onProposeJumpToSection?.(data)
                break

            // Phase 4.5: await_review-Lifecycle. Server-Loop pausiert auf
            // dem await_review-Tool und schickt diese beiden Marker — der
            // SSE-Stream bleibt waehrenddessen offen, und der Client darf
            // Verdicts via reportReviewDone reinreichen.
            case 'await_review_started':
                this._awaitReviewActive = true
                handlers?.onAwaitReviewStarted?.(data)
                break
            case 'await_review_done':
                this._awaitReviewActive = false
                handlers?.onAwaitReviewDone?.(data)
                break

            case 'model_fallback': handlers?.onModelFallback?.(data); break
            case 'iteration_limit': handlers?.onIterationLimit?.(); break
            case 'rate_limit':     handlers?.onRateLimit?.(data); break
            case 'aborted':        this._awaitReviewActive = false; handlers?.onAborted?.(data); break
            case 'error':          this._awaitReviewActive = false; handlers?.onError?.(new Error(data?.message || 'Unknown error')); break
            case 'done':           this._awaitReviewActive = false; handlers?.onDone?.(data); break
        }
    }
}

/**
 * Parser fuer ein einzelnes SSE-Event aus einem Text-Block.
 */
export function parseSSEEvent(raw) {
    let event = ''
    let data = ''
    for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7)
        else if (line.startsWith('data: ')) data = line.slice(6)
    }
    try {
        return { event, data: data ? JSON.parse(data) : {} }
    } catch {
        return { event, data: {} }
    }
}
